import {
  Box,
  Divider,
  Flex,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';
import { CSVLink } from 'react-csv';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  CreditAgreement,
  FundingRequest,
} from '@finley-technologies/finley-types';

import { BorrowingBaseReportGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  aggregateVerificationBorrowingBaseWaiverImpactTotals,
  concentrationLimitPropertyKeyToConcentrationFormat,
} from './utils';
import { FormulaContent } from '~/core/routes/calculation-terminologies/CalculationTerminologiesTableComponents';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';

const waiverImpactTableDataKeyToHeader: {
  key: string;
  label: string;
  popover?: { description: string; formula: string };
}[] = [
  { key: 'concentrationPropertyKey', label: 'Concentration Test' },
  { key: 'concentrationAmountWaived', label: 'Waived Concentration Amount' },
  {
    key: 'concentrationPreWaivers',
    label: 'Pre-Waiver Concentration',
    popover: {
      description:
        'This is an estimate. It does not factor in preceding or proceeding waterfall kickouts for receivables that qualify for multiple concentration tests.',
      formula:
        '(Post-Waiver Concentration Amount + Waived Concentration Amount) / Total Eligible Exposure',
    },
  },
  { key: 'concentrationPostWaivers', label: 'Post-waiver Concentration' },
];

const WaiverImpactConcentrationHeaderPopover = ({
  description,
  formula,
}: {
  description: string;
  formula: string;
}) => {
  return (
    <>
      <Popover>
        <PopoverTrigger>
          <Flex cursor="pointer">
            <Icon icon="circle-info" color={colors.info} />
          </Flex>
        </PopoverTrigger>
        <PopoverContent
          backgroundColor={colors.gray[800]}
          color="white"
          width="400px"
          border="0"
          boxShadow="0px 1px 3px 0px rgba(0, 0, 0, 0.30), 0px 4px 8px 0px rgba(0, 0, 0, 0.15)"
          onClick={(event) => {
            event.stopPropagation();
          }}
          cursor="text"
        >
          <PopoverCloseButton />
          <PopoverBody textAlign="left">
            <Box p="8px">
              <Text fontWeight={600}>Note</Text>
              <Divider my="8px" />
              <Text
                fontWeight={400}
                fontSize="14px"
                textTransform="none"
                mb="16px"
              >
                {description}
              </Text>
              <Box>
                <Text fontWeight={600}>Formula</Text>
                <Divider my="8px" />
                <FormulaContent cellValue={formula} />
              </Box>
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </>
  );
};

type VerificationReportWaiverImpactConcentrationProps = {
  concentrationWaiverImpact: FundingRequest.VerificationBorrowingBaseWaiverImpact;
  calcAgentMechanics: CreditAgreement.DomainEntity.CalcAgentMechanicsWithEntities;
  borrowingBaseReport: BorrowingBaseReportGQLType;
  waiverImpact: {
    eligibilityWaiverImpact: FundingRequest.VerificationBorrowingBaseWaiverImpact;
    concentrationWaiverImpact: FundingRequest.VerificationBorrowingBaseWaiverImpact;
  };
};
export const VerificationReportWaiverImpactConcentration = ({
  concentrationWaiverImpact,
  calcAgentMechanics,
  borrowingBaseReport,
  waiverImpact,
}: VerificationReportWaiverImpactConcentrationProps) => {
  const waiverImpactTotals = aggregateVerificationBorrowingBaseWaiverImpactTotals(
    { waiverImpact, borrowingBaseReport },
  );

  const { totalConcentrationWaived } = waiverImpactTotals;

  const concentrationKeyToFormatMap = concentrationLimitPropertyKeyToConcentrationFormat(
    calcAgentMechanics,
  );

  const columnWidth = `${100 / _.size(waiverImpactTableDataKeyToHeader)}%`;
  const filename = `concentration-waiver-impact-${commonDate.currentDateStr()}.csv`;

  return (
    <>
      <Flex verticalAlign="middle">
        <Text fontWeight={500} mb="24px">{`Total Receivables Waived: ${_.size(
          concentrationWaiverImpact,
        )}`}</Text>
        <Spacer />
        {/* !TODO @eldarshamukhmedov react-csv is no longer maintained, needs to be replaced */}
        <CSVLink
          data={totalConcentrationWaived}
          headers={waiverImpactTableDataKeyToHeader}
          filename={filename}
        >
          <Flex alignItems="baseline">
            <Text color={colors.blue[400]} fontSize="14px" mr="10px">
              Download CSV
            </Text>
            <Icon icon="arrow-down-to-bracket" color={colors.blue[400]} />
          </Flex>
        </CSVLink>
      </Flex>
      <Box maxH="800px" overflowY="auto">
        <Table>
          <Thead>
            <Tr>
              {_.map(waiverImpactTableDataKeyToHeader, (header) => (
                <Th
                  fontSize="16px"
                  fontWeight={500}
                  color={colors.black[95]}
                  w={columnWidth}
                  key={header.key}
                  cursor="default"
                >
                  <Flex alignItems="center" style={{ gap: '8px' }}>
                    <Text>{header.label}</Text>
                    {header.popover && (
                      <WaiverImpactConcentrationHeaderPopover
                        description={header.popover.description}
                        formula={header.popover.formula}
                      />
                    )}
                  </Flex>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {_.map(totalConcentrationWaived, (concentrationTest) => {
              const {
                concentrationPropertyKey,
                concentrationAmountWaived,
                concentrationAmountNotWaived,
                concentrationPreWaivers,
                concentrationPostWaivers,
                totalEligibleExposure,
              } = concentrationTest;

              const concentrationFormat =
                concentrationKeyToFormatMap[concentrationPropertyKey];

              const preWaiverConcentrationTooltipLabel = concentrationAmountNotWaived
                ? `(${commonUtils.numberToCurrencyWithSymbol({
                    number: concentrationAmountNotWaived,
                  })} + ${commonUtils.numberToCurrencyWithSymbol({
                    number: concentrationAmountWaived,
                  })}) / ${commonUtils.numberToCurrencyWithSymbol({
                    number: totalEligibleExposure,
                  })}`
                : '';

              return (
                <Tr key={concentrationPropertyKey}>
                  <Td>{commonUtils.toTitleCase(concentrationPropertyKey)}</Td>
                  <Td>
                    {commonUtils.numberToCurrencyWithSymbol({
                      number: concentrationAmountWaived,
                    })}
                  </Td>
                  <Td>
                    {(() => {
                      if (!_.isNumber(concentrationPreWaivers)) {
                        return '-';
                      }
                      return (
                        <Tooltip label={preWaiverConcentrationTooltipLabel}>
                          <Text>
                            {concentrationFormat === 'percentage'
                              ? commonUtils.numberToFormattedString(
                                  concentrationPreWaivers,
                                  'percentage',
                                  false,
                                )
                              : concentrationPreWaivers}
                          </Text>
                        </Tooltip>
                      );
                    })()}
                  </Td>
                  <Td fontWeight={500}>
                    {(() => {
                      if (!_.isNumber(concentrationPostWaivers)) {
                        return '-';
                      }
                      return concentrationFormat === 'percentage'
                        ? commonUtils.numberToFormattedString(
                            concentrationPostWaivers,
                            'percentage',
                            false,
                          )
                        : concentrationPostWaivers;
                    })()}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};
